/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Security question from Auth Extension with its possible answers. */
export type SecurityQuestion = { id: number; label: string; options: { id: string; value: string }[] };

/** Option chosen for a question, in the format Commerce Cloud forwards to Auth Extension. */
export type SecurityAnswer = { id: number; option: string };

/** Name of the form field that holds the option chosen for a question. */
export function getAnswerFieldName(questionId: number): string {
    return `question-${questionId}`;
}

function isString(value: unknown): value is string {
    return typeof value === 'string';
}

/**
 * Reads the security questions the questions form posts back (JSON) so the screen can be shown again
 * after a wrong answer. Keeps only well-formed questions; Auth Extension validates the answers.
 */
export function parseSecurityQuestions(json: string | undefined): SecurityQuestion[] {
    if (!json) {
        return [];
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return [];
    }

    if (!Array.isArray(parsed)) {
        return [];
    }

    return parsed.flatMap((entry: unknown) => {
        const question = entry as Partial<SecurityQuestion> | null;
        if (
            !question ||
            typeof question.id !== 'number' ||
            !isString(question.label) ||
            !Array.isArray(question.options)
        ) {
            return [];
        }
        const options = question.options.filter((option) => option && isString(option.id) && isString(option.value));
        return options.length ? [{ id: question.id, label: question.label, options }] : [];
    });
}

/**
 * Reads the chosen option of every question. Returns null if a question has no answer or the
 * answer isn't one of its options.
 */
export function readSecurityAnswers(formData: FormData, questions: SecurityQuestion[]): SecurityAnswer[] | null {
    const answers: SecurityAnswer[] = [];

    for (const question of questions) {
        const option = formData.get(getAnswerFieldName(question.id))?.toString();
        if (!option || !question.options.some(({ id }) => id === option)) {
            return null;
        }
        answers.push({ id: question.id, option });
    }

    return answers.length ? answers : null;
}
